import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import '../../css/Pages.css';
import ScientificArticlesMngtListing from '../../components/ScientificManagement/ScientificArticlesMngt/ScientificArticlesMngtListing';
import ScientificArticleMngtForm from '../../components/ScientificManagement/ScientificArticlesMngt/ScientificArticlesMngtForm';
import { useConfirmDeleteDialog } from '../../hooks/useConfirmDeleteDialog';
import { downloadExcel } from '../../utils/excelUtils';
import { COUNTRIES_DATA } from '../../data/countriesData';
import DuplicateWarningModal from '../../components/DuplicateWarningModal';
import { AutoComplete } from 'primereact/autocomplete';

const superCleanString = (str) => {
    if (!str) return '';
    return str
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .replace(/\s+/g, ' ')
        .toLowerCase()
        .trim();
};

const SearchableFocusDropdown = ({ value, options, onChange, placeholder, disabled, panelUniqueClass, className, style }) => {
    const dropdownRef = useRef(null);
    const focusTrapRef = useRef(null);

    const handleShow = () => {
        setTimeout(() => {
            try {
                const panel = document.querySelector(`.${panelUniqueClass}`);
                if (panel) {
                    const filterInput = panel.querySelector('.p-dropdown-filter');
                    if (filterInput && typeof filterInput.focus === 'function') {
                        filterInput.focus();

                        focusTrapRef.current = (e) => {
                            if (e.target !== filterInput) {
                                filterInput.focus();
                            }
                        };

                        panel.addEventListener('focusin', focusTrapRef.current);
                    }
                }
            } catch (err) {
                console.warn("Focus error:", err);
            }
        }, 100);
    };

    const handleHide = () => {
        const panel = document.querySelector(`.${panelUniqueClass}`);
        if (panel && focusTrapRef.current) {
            panel.removeEventListener('focusin', focusTrapRef.current);
        }
    };

    return (
        <Dropdown
            ref={dropdownRef}
            value={value}
            options={options}
            onChange={onChange}
            filter
            filterBy="label,searchKey"
            placeholder={placeholder}
            className={className || "custom-dropdown"}
            panelClassName={panelUniqueClass}
            style={{ width: '100%' }}
            panelStyle={{ width: 'auto', minWidth: '100%', maxWidth: '100%', marginLeft: '10%' }}
            appendTo={document.body}
            onShow={handleShow}
            onHide={handleHide}
            disabled={disabled}
            emptyFilterMessage="Không tìm thấy kết quả"
        />
    );
};

const ScientificArticlesMngt = () => {
    const today = useMemo(() => new Date().toISOString().split('T')[0], []);
    const toast = useRef(null);

    const initialForm = useMemo(() => ({
        UserId: '', Title: '', Summary: '', Keywords: '', LanguageName: '',
        JournalCategory: '',
        JournalId: null, JournalName: '', ISSN_ISBN: '', JournalType: '', JournalScore: 0, JournalStatus: '',
        ResearchFieldId: '', ArticleTypeId: '', IssueNumber: '', PageRange: '', Publisher: '', PublicationDate: today,
        IsInsideProject: false, ProjectCode: '', TotalAuthors: 1, PrimaryAuthors: 1, ContactAuthors: 1, OriginalAuthors: '',
        BonusCoefficient: '1.0', ArticleUrl: '', ScimagoUrl: '', InternationalProofUrl: '', EvidenceFileName: '', MembersJSON: '[]'
    }), [today]);

    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [modalMode, setModalMode] = useState(null);
    const isModalOpen = modalMode !== null;
    const [formData, setFormData] = useState(initialForm);
    const [members, setMembers] = useState([]);
    const [editId, setEditId] = useState(null);

    const [isEditRequestModalOpen, setIsEditRequestModalOpen] = useState(false);
    const [editRequestContent, setEditRequestContent] = useState('');
    const [selectedArticleForEdit, setSelectedArticleForEdit] = useState(null);

    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const [rejectArticleId, setRejectArticleId] = useState(null);
    const [rejectReason, setRejectReason] = useState('');

    const [users, setUsers] = useState([]);
    const [researchFields, setResearchFields] = useState([]);
    const [articleTypes, setArticleTypes] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [jobTitles, setJobTitles] = useState([]);
    const [degrees, setDegrees] = useState([]);
    const [journalTypes, setJournalTypes] = useState([]);
    const [countries, setCountries] = useState([]);

    const [duplicateGroups, setDuplicateGroups] = useState([]);
    const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
    const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);

    const [filters, setFilters] = useState({
        department: '', authorId: null, journalType: '', keyword: '', jobTitle: '', degree: '', status: '',
        fromDate: null, toDate: null
    });

    const [sortConfig, setSortConfig] = useState({ key: 'Title', direction: 'asc' });

    const [selectedAuthor, setSelectedAuthor] = useState(null);
    const [filteredAuthors, setFilteredAuthors] = useState([]);

    const searchAuthor = (event) => {
        const query = superCleanString(event.query);
        let _filteredAuthors;

        if (!query.trim().length) {
            _filteredAuthors = [...filterUserOptions];
        } else {
            _filteredAuthors = filterUserOptions.filter((user) => {
                return user.searchKey.includes(query);
            });
        }
        setFilteredAuthors(_filteredAuthors);
    };

    const journalTypeOptions = useMemo(() => {
        return journalTypes.map(jt => ({
            label: jt.Name,
            value: jt.Name,
            searchKey: superCleanString(jt.Name)
        }));
    }, [journalTypes]);

    const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [previewExportData, setPreviewExportData] = useState(null);
    const [exportFilters, setExportFilters] = useState({
        fromDate: null, toDate: null, qRanking: '', userId: ''
    });

    const [isDesktop, setIsDesktop] = useState(true);

    useEffect(() => {
        const handleResize = () => setIsDesktop(window.innerWidth > 992);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const { confirmDeleteDialog } = useConfirmDeleteDialog();

    const currentUser = useMemo(() => JSON.parse(localStorage.getItem('user')) || {}, []);
    const userRoleName = useMemo(() => (currentUser.RoleName || '').toLowerCase(), [currentUser.RoleName]);

    const isManager = useMemo(() =>
        currentUser.RoleId === 1 || userRoleName === 'quản trị viên' ||
        currentUser.RoleId === 11 || userRoleName.includes('quản lý khoa'),
        [userRoleName, currentUser.RoleId]
    );

    const canModify = useMemo(() =>
        currentUser.RoleId === 1 || userRoleName === 'quản trị viên' ||
        ((currentUser.RoleId === 11 || userRoleName.includes('quản lý khoa')) && currentUser.IsDelegatedAdmin === true),
        [userRoleName, currentUser.RoleId, currentUser.IsDelegatedAdmin]
    );

    const authHeaders = useMemo(() => ({
        'X-User-Role-Id': currentUser.RoleId ? currentUser.RoleId.toString() : '',
        'X-User-Dept-Id': currentUser.DepartmentId ? currentUser.DepartmentId.toString() : '',
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
    }), [currentUser]);

    const userOptions = useMemo(() => {
        return users.map(u => {
            const labelStr = `${u.LastName || ''} ${u.FirstName || ''} - ${u.Email || ''}`.trim();
            return {
                label: labelStr,
                searchKey: superCleanString(labelStr),
                value: u.Id
            };
        });
    }, [users]);

    const userFilterTemplate = (option) => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0' }}>
                <span style={{ fontWeight: 'bold', color: '#333' }}>{option.fullName}</span>
                <span style={{ fontSize: '12px', color: '#666' }}>
                    {option.email} {option.dept ? ` - ${option.dept}` : ''}
                </span>
            </div>
        );
    };

    const filterUserOptions = useMemo(() => {
        return users.map(u => {
            const fullName = `${u.LastName || ''} ${u.FirstName || ''}`.trim();
            const email = u.Email || '';
            const dept = u.DepartmentName || '';
            const rawSearchStr = `${fullName} ${email} ${dept}`;

            return {
                label: fullName,
                searchKey: superCleanString(rawSearchStr),
                value: u.Id,
                fullName: fullName,
                email: email,
                dept: dept
            };
        });
    }, [users]);

    const filteredData = useMemo(() => {
        let allowedDeptNames = [];
        if (filters.department) {
            const selectedDept = departments.find(d => d.Name.trim() === filters.department.trim());

            if (selectedDept) {
                allowedDeptNames.push(selectedDept.Name.trim());

                const findChildren = (parentId) => {
                    const children = departments.filter(d => d.ParentId === parentId);
                    children.forEach(child => {
                        allowedDeptNames.push(child.Name.trim());
                        findChildren(child.Id);
                    });
                };

                findChildren(selectedDept.Id);
            } else {
                allowedDeptNames.push(filters.department.trim());
            }
        }

        let result = data.filter(item => {
            if (filters.department) {
                const itemDeptName = item.DepartmentName ? item.DepartmentName.trim() : "";
                if (!allowedDeptNames.includes(itemDeptName)) {
                    return false;
                }
            }

            if (filters.journalType && item.JournalType !== filters.journalType) return false;
            if (filters.jobTitle && item.JobTitleName !== filters.jobTitle) return false;
            if (filters.degree && item.DegreeName !== filters.degree) return false;
            if (filters.status && item.Status !== filters.status) return false;

            if (item.PublicationDate) {
                const itemDate = new Date(item.PublicationDate);
                itemDate.setHours(0, 0, 0, 0);

                if (filters.fromDate) {
                    const filterFrom = new Date(filters.fromDate);
                    filterFrom.setHours(0, 0, 0, 0);
                    if (itemDate < filterFrom) return false;
                }
                if (filters.toDate) {
                    const filterTo = new Date(filters.toDate);
                    filterTo.setHours(0, 0, 0, 0);
                    if (itemDate > filterTo) return false;
                }
            } else if (filters.fromDate || filters.toDate) {
                return false;
            }

            if (filters.authorId) {
                const targetId = filters.authorId.toString();
                let isMatch = item.UserId?.toString() === targetId;

                if (!isMatch) {
                    try {
                        const mList = JSON.parse(item.MembersJSON || '[]');
                        isMatch = mList.some(m => (m.UserId || m.userId || m.Id)?.toString() === targetId);
                    } catch (e) {
                        isMatch = false;
                    }
                }

                if (!isMatch) return false;
            }

            if (filters.keyword) {
                const searchKeyword = superCleanString(filters.keyword);
                const articleTitle = superCleanString(item.Title);
                const journalName = superCleanString(item.JournalName);

                if (!articleTitle.includes(searchKeyword) && !journalName.includes(searchKeyword)) {
                    return false;
                }
            }

            return true;
        });

        if (sortConfig !== null) {
            result.sort((a, b) => {
                if (sortConfig.key === 'PublicationDate') {
                    const dateA = a.PublicationDate ? new Date(a.PublicationDate).getTime() : 0;
                    const dateB = b.PublicationDate ? new Date(b.PublicationDate).getTime() : 0;
                    if (dateA < dateB) return sortConfig.direction === 'asc' ? -1 : 1;
                    if (dateA > dateB) return sortConfig.direction === 'asc' ? 1 : -1;
                    return 0;
                } else {
                    const valA = (a[sortConfig.key] || '').toString().trim();
                    const valB = (b[sortConfig.key] || '').toString().trim();
                    const comparison = valA.localeCompare(valB, 'vi', { sensitivity: 'base' });
                    return sortConfig.direction === 'asc' ? comparison : -comparison;
                }
            });
        }

        return result;

    }, [data, filters, departments, sortConfig]);

    const handleFetchDuplicates = async () => {
        setIsCheckingDuplicates(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/detect-duplicates?type=article`, {
                method: 'GET',
                headers: authHeaders
            });

            if (res.ok) {
                const groups = await res.json();
                setDuplicateGroups(groups);
                setIsDuplicateModalOpen(true);

                if (groups.length === 0) {
                    toast.current?.show({ severity: 'info', summary: 'Thông báo', detail: 'Không phát hiện bài báo nào trùng lặp!' });
                }
            } else {
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không thể lấy dữ liệu trùng lặp từ hệ thống!' });
            }
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Mất kết nối đến máy chủ!' });
        } finally {
            setIsCheckingDuplicates(false);
        }
    };

    const fetchDropdowns = useCallback(async () => {
        const baseUrl = process.env.REACT_APP_API_BASE_URL;

        try {
            const [uRes, rfRes, atRes, deptRes, jobRes, degRes, jtRes] = await Promise.all([
                fetch(`${baseUrl}/user`, { headers: authHeaders }),
                fetch(`${baseUrl}/researchfield`, { headers: authHeaders }),
                fetch(`${baseUrl}/articletype`, { headers: authHeaders }),
                fetch(`${baseUrl}/department`, { headers: authHeaders }),
                fetch(`${baseUrl}/jobtitle`, { headers: authHeaders }),
                fetch(`${baseUrl}/educationlevel`, { headers: authHeaders }),
                fetch(`${baseUrl}/journaltype`, { headers: authHeaders })
            ]);

            if (uRes.ok) setUsers(await uRes.json());
            if (rfRes.ok) setResearchFields(await rfRes.json());
            if (atRes.ok) setArticleTypes(await atRes.json());
            if (deptRes.ok) setDepartments(await deptRes.json());
            if (jobRes.ok) setJobTitles(await jobRes.json());
            if (degRes.ok) setDegrees(await degRes.json());

            if (jtRes.ok) {
                const jtData = await jtRes.json();
                setJournalTypes(Array.isArray(jtData) ? jtData : (jtData?.data || []));
            }
        } catch (error) {
            console.error("Lỗi tải danh mục hệ thống:", error);
        }

        setCountries(COUNTRIES_DATA);

    }, [authHeaders]);

    const fetchData = useCallback(async (isBackground = false) => {
        if (!currentUser?.Id) return;
        if (!isBackground) setIsLoading(true);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/scientificarticlemngt?userId=${currentUser.Id}&isAdmin=${isManager}`,
                { method: 'GET', headers: authHeaders }
            );
            if (response.ok) {
                const result = await response.json();
                setData(result);
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi tải dữ liệu bài báo!', life: 3000 });
        } finally {
            if (!isBackground) setIsLoading(false);
        }
    }, [currentUser?.Id, isManager, authHeaders]);

    useEffect(() => {
        fetchData();
        fetchDropdowns();
    }, [fetchData, fetchDropdowns]);

    const handleFilterChange = useCallback((e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    }, []);

    const checkAndFetchMissingUsers = useCallback(async (parsedMembers) => {
        const missingIds = parsedMembers
            .map(m => m.userId || m.UserId || m.Id || m.id)
            .filter(id => id && !users.some(u => u.Id.toString() === id.toString()));

        if (missingIds.length > 0) {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user?includeIds=${missingIds.join(',')}`, {
                    headers: authHeaders
                });
                if (res.ok) {
                    const extraUsers = await res.json();
                    setUsers(prevUsers => {
                        const merged = [...prevUsers];
                        extraUsers.forEach(eu => {
                            if (!merged.some(u => u.Id === eu.Id)) merged.push(eu);
                        });
                        return merged;
                    });
                }
            } catch (error) {
                console.error("Lỗi tải người dùng bổ sung:", error);
            }
        }
    }, [users, authHeaders]);

    const handleEdit = useCallback(async (item) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn chỉ có quyền xem, không được phép sửa dữ liệu!', life: 3000 });
        }
        if (item.Status === 'Đã duyệt') {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Không thể sửa khi dữ liệu đã duyệt. Hãy hủy duyệt trước!', life: 4000 });
        }

        let parsedMembers = [];
        try { parsedMembers = JSON.parse(item.MembersJSON || '[]'); } catch (e) { parsedMembers = []; }

        await checkAndFetchMissingUsers(parsedMembers);

        setEditId(item.Id);
        setFormData({
            ...initialForm,
            ...item,
            UserId: item.UserId || '',
            ResearchFieldId: item.ResearchFieldId || '',
            ArticleTypeId: item.ArticleTypeId || '',
            IsInsideProject: !!item.IsInsideProject
        });
        setMembers(parsedMembers);
        setModalMode('edit');
    }, [canModify, initialForm, checkAndFetchMissingUsers]);

    const handleViewDetail = useCallback(async (item) => {
        let parsedMembers = [];
        try { parsedMembers = JSON.parse(item.MembersJSON || '[]'); } catch (e) { parsedMembers = []; }

        await checkAndFetchMissingUsers(parsedMembers);

        setEditId(item.Id);
        setFormData({
            ...initialForm,
            ...item,
            UserId: item.UserId || '',
            ResearchFieldId: item.ResearchFieldId || '',
            ArticleTypeId: item.ArticleTypeId || '',
            IsInsideProject: !!item.IsInsideProject
        });
        setMembers(parsedMembers);
        setModalMode('view');
    }, [initialForm, checkAndFetchMissingUsers]);

    const closeModal = useCallback(() => {
        setModalMode(null);
        setFormData(initialForm);
        setMembers([]);
        setEditId(null);
    }, [initialForm]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (modalMode === 'view') return;
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn không có quyền thao tác dữ liệu!', life: 3000 });
        }
        if (editId && data.some(d => d.Id === editId && d.Status === 'Đã duyệt')) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Dữ liệu đã duyệt, không thể lưu!', life: 3000 });
        }
        if (!currentUser?.Id) {
            return toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Hết phiên đăng nhập!', life: 3000 });
        }

        let finalUserId = members[0]?.userId || members[0]?.UserId || members[0]?.Id || formData.UserId || currentUser.Id;

        const payload = {
            ...formData,
            UserId: parseInt(finalUserId),
            CreatedByEmail: currentUser.Email,
            MembersJSON: JSON.stringify(members),
            Id: editId || 0
        };

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/scientificarticlemngt`, {
                method: editId ? 'PUT' : 'POST',
                headers: { ...authHeaders, 'Content-Type': 'application/json; charset=UTF-8' },
                body: JSON.stringify(payload)
            });

            const result = await response.json().catch(() => ({ status: 'error', message: 'Lỗi máy chủ không xác định!' }));

            if (response.ok && result.status !== 'error') {
                toast.current?.show({ severity: 'success', summary: 'Thành công', detail: editId ? 'Cập nhật thành công!' : 'Thêm mới thành công!', life: 3000 });
                fetchData(true);
                closeModal();
            } else {
                toast.current?.show({ severity: 'error', summary: 'Không thể lưu', detail: result.message || 'Lưu dữ liệu thất bại!', life: 6000 });
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        }
    };

    const handleStatusChange = useCallback(async (id, newStatus) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn không có quyền phê duyệt/từ chối!', life: 3000 });
        }
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/scientificarticlemngt?userId=${currentUser.Id}&isAdmin=${isManager}`, {
                method: 'PATCH',
                headers: { ...authHeaders, 'Content-Type': 'application/json; charset=UTF-8' },
                body: JSON.stringify({ id, status: newStatus })
            });

            if (response.ok) {
                const messages = { 'Đã duyệt': "Phê duyệt thành công!", 'Đã gửi duyệt': "Đã hủy phê duyệt thành công!" };
                toast.current?.show({ severity: 'success', summary: 'Thành công', detail: messages[newStatus] || 'Cập nhật trạng thái thành công!', life: 3000 });
                fetchData(true);
            } else {
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Thao tác thất bại!', life: 3000 });
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        }
    }, [canModify, currentUser.Id, isManager, authHeaders, fetchData]);

    const handleBulkStatusChange = async (ids, newStatus) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn không có quyền phê duyệt!', life: 3000 });
        }

        if (newStatus === 'Đã duyệt') {
            const invalidItems = data.filter(d => ids.includes(d.Id) && d.Status === 'Đã duyệt');
            if (invalidItems.length > 0) {
                return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng bỏ chọn các hồ sơ đã duyệt để thao tác!', life: 4000 });
            }
        } else if (newStatus === 'Đã gửi duyệt') {
            const invalidItems = data.filter(d => ids.includes(d.Id) && d.Status !== 'Đã duyệt');
            if (invalidItems.length > 0) {
                return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Chỉ có thể hủy đối với hồ sơ có trạng thái "Đã duyệt"!', life: 4000 });
            }
        }

        setIsLoading(true);
        try {
            const promises = ids.map(id =>
                fetch(`${process.env.REACT_APP_API_BASE_URL}/scientificarticlemngt?userId=${currentUser.Id}&isAdmin=${isManager}`, {
                    method: 'PATCH',
                    headers: { ...authHeaders, 'Content-Type': 'application/json; charset=UTF-8' },
                    body: JSON.stringify({ id, status: newStatus })
                })
            );

            const results = await Promise.all(promises);
            const allOk = results.every(res => res.ok);

            if (allOk) {
                toast.current?.show({
                    severity: 'success', summary: 'Thành công',
                    detail: newStatus === 'Đã duyệt' ? `Đã phê duyệt ${ids.length} mục!` : `Đã hủy duyệt ${ids.length} mục!`, life: 3000
                });
            } else {
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: "Một số thao tác thất bại!", life: 3000 });
            }
            fetchData(true);
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenReject = useCallback((item) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Bạn không có quyền từ chối!', life: 3000 });
        }
        setRejectArticleId(item.Id);
        setRejectReason('');
        setRejectModalOpen(true);
    }, [canModify]);

    const handleSendReject = async (e) => {
        e.preventDefault();
        if (!rejectReason.trim()) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng nhập lý do từ chối!', life: 3000 });
        }
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/scientificarticlemngt?userId=${currentUser.Id}&isAdmin=${isManager}`, {
                method: 'PATCH',
                headers: { ...authHeaders, 'Content-Type': 'application/json; charset=UTF-8' },
                body: JSON.stringify({ id: rejectArticleId, status: 'Từ chối', note: rejectReason })
            });

            if (response.ok) {
                toast.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Đã từ chối và ghi nhận lý do!', life: 3000 });
                setRejectModalOpen(false);
                fetchData(true);
            } else {
                const err = await response.json();
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: err.message || 'Từ chối thất bại!', life: 3000 });
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        }
    };

    const handleOpenRequestEdit = useCallback((item) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn không có quyền yêu cầu chỉnh sửa!', life: 3000 });
        }
        setSelectedArticleForEdit(item);
        setEditRequestContent('');
        setIsEditRequestModalOpen(true);
    }, [canModify]);

    const handleSendEditRequest = async (e) => {
        e.preventDefault();
        if (!editRequestContent.trim()) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng nhập nội dung yêu cầu!', life: 3000 });
        }
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/scientificarticlemngt?userId=${currentUser.Id}&isAdmin=${isManager}`, {
                method: 'PATCH',
                headers: { ...authHeaders, 'Content-Type': 'application/json; charset=UTF-8' },
                body: JSON.stringify({ id: selectedArticleForEdit.Id, status: 'Yêu cầu chỉnh sửa', note: editRequestContent })
            });

            if (response.ok) {
                toast.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Đã gửi Email yêu cầu chỉnh sửa!', life: 3000 });
                setIsEditRequestModalOpen(false);
                fetchData(true);
            } else {
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Gửi yêu cầu thất bại!', life: 3000 });
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        }
    };

    const handleDelete = useCallback((id) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn chỉ có quyền xem, không được phép xóa!', life: 3000 });
        }
        if (data.some(d => d.Id === id && d.Status === 'Đã duyệt')) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Không thể xóa bài báo đã duyệt!', life: 4000 });
        }

        confirmDeleteDialog({
            header: 'Xác nhận xóa',
            message: 'Bạn có chắc chắn muốn xóa bài báo này? Dữ liệu bên Giảng viên cũng sẽ bị xóa.',
            accept: async () => {
                try {
                    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/scientificarticlemngt?id=${id}&userId=${currentUser.Id}&isAdmin=${isManager}`, {
                        method: 'DELETE',
                        headers: authHeaders
                    });
                    if (response.ok) {
                        toast.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Xóa thành công!' });
                        fetchData(true);
                    } else {
                        toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Xóa thất bại!' });
                    }
                } catch (e) {
                    toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi máy chủ!' });
                }
            }
        });
    }, [canModify, data, currentUser.Id, isManager, authHeaders, fetchData, confirmDeleteDialog]);

    const handleBulkDelete = (ids) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn chỉ có quyền xem, không được phép xóa!', life: 3000 });
        }
        const invalidItems = data.filter(d => ids.includes(d.Id) && d.Status === 'Đã duyệt');
        if (invalidItems.length > 0) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng bỏ chọn các bài báo đã duyệt khỏi danh sách cần xóa!', life: 4000 });
        }

        confirmDeleteDialog({
            header: 'Xác nhận xóa hàng loạt',
            message: `Bạn có chắc chắn muốn xóa ${ids.length} bài báo đã chọn? Dữ liệu bên Giảng viên cũng sẽ bị xóa.`,
            accept: async () => {
                setIsLoading(true);
                try {
                    const promises = ids.map(id =>
                        fetch(`${process.env.REACT_APP_API_BASE_URL}/scientificarticlemngt?id=${id}&userId=${currentUser.Id}&isAdmin=${isManager}`, {
                            method: 'DELETE',
                            headers: authHeaders
                        })
                    );

                    const results = await Promise.all(promises);
                    const allOk = results.every(res => res.ok);

                    if (allOk) {
                        toast.current?.show({ severity: 'success', summary: 'Thành công', detail: `Đã xóa ${ids.length} bản ghi!` });
                    } else {
                        toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Một số bản ghi xóa thất bại!' });
                    }
                    fetchData(true);
                } catch (error) {
                    toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối!' });
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    const executeExport = useCallback((dataToExport, fileSuffix) => {
        if (dataToExport.length === 0) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Không có dữ liệu để xuất!', life: 3000 });
        }

        const internationalArticles = [];
        const domesticArticles = [];
        const qStats = { Q1: 0, Q2: 0, Q3: 0, Q4: 0, Khac: 0 };

        const removeAccents = (str) => {
            return str ? str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase() : '';
        };

        const checkIsInternational = (item) => {
            const typeStr = removeAccents(item.JournalType || '');
            const nameStr = removeAccents(item.JournalName || '');
            const catStr = removeAccents(item.JournalCategory || '');
            const titleStr = item.Title || '';

            const qRank = (item.QRanking || '').trim().toUpperCase();
            const catRaw = (item.JournalCategory || '').trim().toUpperCase();

            const hasVietnameseTitle = /[àáạảãâầấậẩẫăằắặẳẵèéẹẻẽêềếệểễìíịỉĩòóọỏõôồốộổỗơờớợởỡùúụủũưừứựửữỳýỵỷỹđ]/i.test(titleStr);

            const strictDomesticJournals = [
                'kinh te va phat trien', 'khoa hoc kinh te', 'kh & cn', 'khoa hoc va cong nghe',
                'dai hoc', 'ky yeu', 'quoc gia', 'dhdn', 'thuong mai', 'quan ly', 'phat trien kinh te',
                'journal of science and technology', 'science and technology development',
                'ho chi minh', 'vnu', 'kinh te va quan ly', 'tap chi vn'
            ];

            if (strictDomesticJournals.some(kw => nameStr.includes(kw))) {
                return false;
            }

            const isQ = ['Q1', 'Q2', 'Q3', 'Q4'];
            if (isQ.includes(qRank) || isQ.includes(catRaw)) return true;

            const strongIntlKeywords = ['scopus', 'scie', 'ssci', 'isi', 'esci', 'springer', 'emerald', 'elsevier', 'mdpi'];
            if (strongIntlKeywords.some(kw => nameStr.includes(kw) || typeStr.includes(kw))) {
                return true;
            }

            if (!hasVietnameseTitle) {
                return true;
            } else {
                return false;
            }
        };

        dataToExport.forEach(item => {
            if (checkIsInternational(item)) {
                internationalArticles.push(item);
            } else {
                domesticArticles.push(item);
            }

            const q = (item.QRanking || '').trim().toUpperCase();
            const catQ = (item.JournalCategory || '').trim().toUpperCase();
            const finalQ = ['Q1', 'Q2', 'Q3', 'Q4'].includes(q) ? q : (['Q1', 'Q2', 'Q3', 'Q4'].includes(catQ) ? catQ : 'Khac');

            if (finalQ !== 'Khac') {
                qStats[finalQ]++;
            } else {
                qStats.Khac++;
            }
        });

        const mapData = (articles) => articles.map((item, index) => ({
            'STT': index + 1,
            'Tên bài báo': item.Title || '',
            'Tác giả': item.OriginalAuthors || '',
            'Đơn vị': item.DepartmentName || '',
            'Phân loại': checkIsInternational(item) ? 'Quốc tế' : 'Trong nước',
            'Danh mục tạp chí': item.JournalCategory || '',
            'Tên tạp chí': item.JournalName || '',
            'Chỉ số (ISSN/ISBN)': item.ISSN_ISBN || '',
            'Loại tạp chí': item.JournalType || '',
            'Q-Ranking': ['Q1', 'Q2', 'Q3', 'Q4'].includes((item.QRanking || '').toUpperCase())
                ? item.QRanking.toUpperCase()
                : (['Q1', 'Q2', 'Q3', 'Q4'].includes((item.JournalCategory || '').toUpperCase()) ? item.JournalCategory.toUpperCase() : ''),
            'Điểm CT': item.JournalScore || 0,
            'Ngày công bố': item.PublicationDate ? new Date(item.PublicationDate).toLocaleDateString('vi-VN') : '',
            'Trạng thái': item.Status || 'Lưu nháp',
            'Người tạo': item.CreatedByEmail || ''
        }));

        const formattedData = [];

        if (internationalArticles.length > 0) {
            formattedData.push({ 'STT': 'I', 'Tên bài báo': `BÀI BÁO QUỐC TẾ (${internationalArticles.length})` });
            formattedData.push(...mapData(internationalArticles));
        }

        if (domesticArticles.length > 0) {
            if (internationalArticles.length > 0) formattedData.push({});

            formattedData.push({ 'STT': 'II', 'Tên bài báo': `BÀI BÁO TRONG NƯỚC (${domesticArticles.length})` });
            formattedData.push(...mapData(domesticArticles));
        }

        formattedData.push({});
        formattedData.push({});
        formattedData.push({ 'STT': '', 'Tên bài báo': 'THỐNG KÊ TỔNG HỢP:' });
        formattedData.push({ 'STT': '', 'Tên bài báo': `- Tổng số bài báo: ${dataToExport.length} (Quốc tế: ${internationalArticles.length}, Trong nước: ${domesticArticles.length})` });
        formattedData.push({ 'STT': '', 'Tên bài báo': `- Thống kê theo chỉ số Q-Ranking:` });
        formattedData.push({ 'STT': '', 'Tên bài báo': `   + Q1: ${qStats.Q1} bài` });
        formattedData.push({ 'STT': '', 'Tên bài báo': `   + Q2: ${qStats.Q2} bài` });
        formattedData.push({ 'STT': '', 'Tên bài báo': `   + Q3: ${qStats.Q3} bài` });
        formattedData.push({ 'STT': '', 'Tên bài báo': `   + Q4: ${qStats.Q4} bài` });
        formattedData.push({ 'STT': '', 'Tên bài báo': `   + Khác/Không phân loại: ${qStats.Khac} bài` });

        const colWidths = [
            { wch: 5 }, { wch: 45 }, { wch: 25 }, { wch: 20 }, { wch: 15 },
            { wch: 20 }, { wch: 35 }, { wch: 20 }, { wch: 20 }, { wch: 15 },
            { wch: 10 }, { wch: 15 }, { wch: 20 }, { wch: 25 }
        ];

        downloadExcel({
            data: formattedData,
            fileName: `ThongKeBaiBao_${fileSuffix}`,
            sheetName: 'ThongKe',
            colWidths
        });
    }, []);

    const handleExportCurrent = () => {
        executeExport(filteredData, 'HienHanh');
        setExportDropdownOpen(false);
    };

    const getFilteredExportData = useCallback(() => {
        let result = [...data];
        if (exportFilters.fromDate) {
            const filterFrom = new Date(exportFilters.fromDate);
            filterFrom.setHours(0, 0, 0, 0);
            result = result.filter(item => item.PublicationDate && new Date(item.PublicationDate) >= filterFrom);
        }
        if (exportFilters.toDate) {
            const filterTo = new Date(exportFilters.toDate);
            filterTo.setHours(23, 59, 59, 999);
            result = result.filter(item => item.PublicationDate && new Date(item.PublicationDate) <= filterTo);
        }
        if (exportFilters.qRanking) result = result.filter(item => item.QRanking === exportFilters.qRanking);
        if (exportFilters.userId) {
            const targetId = exportFilters.userId.toString();
            result = result.filter(item => {
                if (item.UserId?.toString() === targetId) return true;
                try {
                    const mList = JSON.parse(item.MembersJSON || '[]');
                    return mList.some(m => (m.UserId || m.userId || m.Id)?.toString() === targetId);
                } catch (e) { return false; }
            });
        }
        return result;
    }, [data, exportFilters]);

    const handlePreviewAdvancedFilters = () => {
        setPreviewExportData(getFilteredExportData());
    };

    const handleClearAdvancedFilters = () => {
        setExportFilters({ fromDate: null, toDate: null, qRanking: '', userId: '' });
        setPreviewExportData(null);
    };

    const handleExportFromModal = () => {
        executeExport(previewExportData || getFilteredExportData(), 'LocNangCao');
        setIsExportModalOpen(false);
    };

    const renderDeptTree = (parentId = null, level = 0) => {
        return departments
            .filter(d => d.ParentId === parentId)
            .map(d => (
                <React.Fragment key={`filter-dept-${d.Id}`}>
                    <option value={d.Name}>
                        {'\u00A0'.repeat(level * 4)}{level > 0 ? '˪ ' : ''}{d.Name}
                    </option>
                    {renderDeptTree(d.Id, level + 1)}
                </React.Fragment>
            ));
    };

    return (
        <div className="page-container">
            <Toast ref={toast} position="top-right" baseZIndex={99999} style={{ zIndex: 99999 }} />

            <div className="page-header">
                <div className="header-title">
                    <h2 style={{ textTransform: 'uppercase' }}>QUẢN LÝ BÀI BÁO - THAM LUẬN</h2>
                </div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
                {canModify && (
                    <button
                        className="btn-add-new"
                        onClick={() => { setEditId(null); setModalMode('add'); }}
                        style={{ margin: 0, flex: isDesktop ? 'none' : '1 1 200px' }}
                    >
                        <i className="fa-solid fa-plus"></i> Thêm mới
                    </button>
                )}

                <div style={{ position: 'relative', flex: isDesktop ? 'none' : '1 1 200px' }}>
                    <button
                        className="btn-add-new"
                        style={{ backgroundColor: '#1a9953', margin: 0, width: '100%' }}
                        onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
                    >
                        <i className="fa-solid fa-file-excel"></i> Xuất Excel <i className="fa-solid fa-caret-down" style={{ marginLeft: '5px' }}></i>
                    </button>

                    {exportDropdownOpen && (
                        <>
                            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }} onClick={() => setExportDropdownOpen(false)}></div>
                            <div className="custom-dropdown-menu">
                                <div className="custom-dropdown-item" onClick={handleExportCurrent}>
                                    <i className="fa-solid fa-list-check" style={{ color: '#1a9953', width: '20px', fontSize: '16px' }}></i>
                                    Xuất toàn bộ dữ liệu
                                </div>
                                <div className="custom-dropdown-divider"></div>
                                <div className="custom-dropdown-item" onClick={() => {
                                    setIsExportModalOpen(true);
                                    setExportDropdownOpen(false);
                                    setPreviewExportData(null);
                                }}>
                                    <i className="fa-solid fa-filter" style={{ color: '#f39c12', width: '20px', fontSize: '16px' }}></i>
                                    Lọc dữ liệu nâng cao
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <button
                    className="btn-add-new"
                    style={{ backgroundColor: '#e74c3c', margin: 0, opacity: isCheckingDuplicates ? 0.7 : 1 }}
                    onClick={handleFetchDuplicates}
                    disabled={isCheckingDuplicates}
                >
                    {isCheckingDuplicates ? (
                        <span><i className="fa-solid fa-spinner fa-spin" style={{ marginRight: '5px' }}></i> Đang phân tích...</span>
                    ) : (
                        <span><i className="fa-solid fa-triangle-exclamation" style={{ marginRight: '5px' }}></i> Kiểm tra trùng lặp</span>
                    )}
                </button>
            </div>

            <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', marginBottom: '20px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                <p style={{ margin: '0 0 15px 0', fontWeight: 'bold', color: '#555' }}>DANH SÁCH BÀI BÁO - BÁO CÁO</p>

                <div style={{
                    display: isDesktop ? 'grid' : 'flex',
                    gridTemplateColumns: isDesktop ? 'repeat(4, 1fr)' : 'none',
                    flexWrap: isDesktop ? 'nowrap' : 'wrap',
                    gap: '15px'
                }}>
                    <select
                        name="department"
                        className="form-input"
                        style={{ flex: isDesktop ? 'none' : '1 1 200px' }}
                        value={filters.department}
                        onChange={handleFilterChange}
                    >
                        <option value="">Chọn đơn vị</option>
                        {renderDeptTree()}
                    </select>

                    <div style={{ flex: isDesktop ? 'none' : '1 1 200px', width: '100%' }}>
                        <AutoComplete
                            value={selectedAuthor}
                            suggestions={filteredAuthors}
                            completeMethod={searchAuthor}
                            field="label"
                            dropdown
                            placeholder="Họ tên giảng viên"
                            itemTemplate={userFilterTemplate}
                            onChange={(e) => {
                                setSelectedAuthor(e.value);
                                if (e.value && e.value.value) {
                                    setFilters(prev => ({ ...prev, authorId: e.value.value }));
                                } else {
                                    setFilters(prev => ({ ...prev, authorId: null }));
                                }
                            }}
                            style={{ width: '100%', height: '40px' }}
                            inputStyle={{ width: '100%', padding: '0 12px', border: '1px solid #ced4da', borderRadius: '4px', fontFamily: 'inherit' }}
                            panelStyle={{ width: 'auto', minWidth: '350px', maxWidth: '500px' }}
                            emptyMessage="Không tìm thấy giảng viên"
                        />
                    </div>

                    <div style={{ flex: isDesktop ? 'none' : '1 1 200px', width: '100%' }}>
                        <SearchableFocusDropdown
                            value={filters.journalType}
                            options={journalTypeOptions}
                            onChange={(e) => setFilters(prev => ({ ...prev, journalType: e.value }))}
                            placeholder="Loại tạp chí"
                            panelUniqueClass="dropdown-panel-filter-journal-type"
                            className="form-input"
                        />
                    </div>

                    <select name="jobTitle" className="form-input" style={{ flex: isDesktop ? 'none' : '1 1 200px' }} value={filters.jobTitle} onChange={handleFilterChange}>
                        <option value="">Chọn chức danh</option>
                        {jobTitles.map(j => <option key={j.Id} value={j.Name}>{j.Name}</option>)}
                    </select>

                    <select name="degree" className="form-input" style={{ flex: isDesktop ? 'none' : '1 1 200px' }} value={filters.degree} onChange={handleFilterChange}>
                        <option value="">Chọn học vị</option>
                        {degrees.map(dg => <option key={dg.Id} value={dg.Name}>{dg.Name}</option>)}
                    </select>

                    <select name="status" className="form-input" style={{ flex: isDesktop ? 'none' : '1 1 200px' }} value={filters.status} onChange={handleFilterChange}>
                        <option value="">Trạng thái</option>
                        <option value="Lưu nháp">Lưu nháp</option>
                        <option value="Đã gửi duyệt">Đã gửi duyệt</option>
                        <option value="Đã duyệt">Đã duyệt</option>
                        <option value="Từ chối">Từ chối</option>
                        <option value="Yêu cầu chỉnh sửa">Yêu cầu chỉnh sửa</option>
                    </select>

                    <div style={{ flex: isDesktop ? 'none' : '1 1 200px', width: '100%' }}>
                        <Calendar
                            id="fromDate"
                            value={filters.fromDate}
                            onChange={(e) => setFilters(prev => ({ ...prev, fromDate: e.value }))}
                            placeholder="Từ ngày"
                            dateFormat="dd/mm/yy"
                            showIcon
                            className="custom-prime-calendar"
                            style={{ width: '100%', height: '40px' }}
                            inputStyle={{ paddingLeft: '12px' }}
                        />
                    </div>

                    <div style={{ flex: isDesktop ? 'none' : '1 1 200px', width: '100%' }}>
                        <Calendar
                            id="toDate"
                            value={filters.toDate}
                            onChange={(e) => setFilters(prev => ({ ...prev, toDate: e.value }))}
                            placeholder="Đến ngày"
                            dateFormat="dd/mm/yy"
                            showIcon
                            className="custom-prime-calendar"
                            style={{ width: '100%', height: '40px' }}
                            inputStyle={{ paddingLeft: '12px' }}
                        />
                    </div>

                    <div className="search-wrapper" style={{ position: 'relative', flex: isDesktop ? 'none' : '1 1 250px', gridColumn: isDesktop ? 'span 2' : 'none' }}>
                        <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', left: '10px', top: '12px', color: '#888' }}></i>
                        <input type="text" name="keyword" placeholder="Tiêu đề bài báo" className="form-input" style={{ paddingLeft: '35px', width: '100%' }} value={filters.keyword} onChange={handleFilterChange} />
                    </div>
                </div>
            </div>

            <ScientificArticlesMngtListing
                data={filteredData}
                filters={filters}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onApprove={(id) => handleStatusChange(id, 'Đã duyệt')}
                onReject={(id) => handleStatusChange(id, 'Từ chối')}
                onRejectOpen={handleOpenReject}
                onCancelApprove={(id) => handleStatusChange(id, 'Đã gửi duyệt')}
                onRequestEdit={handleOpenRequestEdit}
                onViewDetail={handleViewDetail}
                onBulkApprove={(ids) => handleBulkStatusChange(ids, 'Đã duyệt')}
                onBulkCancelApprove={(ids) => handleBulkStatusChange(ids, 'Đã gửi duyệt')}
                onBulkDelete={handleBulkDelete}
                isLoading={isLoading}
                toast={toast}
                sortConfig={sortConfig}
                requestSort={(key) => {
                    let direction = 'asc';
                    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
                    setSortConfig({ key, direction });
                }}
            />

            <ScientificArticleMngtForm
                isOpen={isModalOpen}
                onClose={closeModal}
                onSubmit={handleSubmit}
                formData={formData}
                setFormData={setFormData}
                isEditing={modalMode === 'edit' || modalMode === 'view'}
                readOnly={modalMode === 'view'}
                users={users}
                fields={researchFields}
                articleTypes={articleTypes}
                members={members}
                setMembers={setMembers}
                countries={countries}
                journalTypes={journalTypes}
                isAdminMode={true}
                toast={toast}
            />

            {isEditRequestModalOpen && (
                <div className="modal-overlay" style={{ zIndex: 1050 }}>
                    <div className="modal-box form-modal-box" style={{ maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <h3 style={{ margin: 0, paddingRight: '15px', lineHeight: '1.4' }}>Yêu cầu chỉnh sửa Bài báo</h3>
                            <button className="close-btn" onClick={() => setIsEditRequestModalOpen(false)} style={{ fontSize: '26px', lineHeight: '1', display: 'flex', flexShrink: 0, marginTop: '-2px' }}>&times;</button>
                        </div>
                        <div className="modal-body" style={{ padding: '20px' }}>
                            <form id="editRequestForm" onSubmit={handleSendEditRequest}>
                                <div className="form-group" style={{ marginBottom: '15px', backgroundColor: '#f8f9fa', padding: '10px', borderRadius: '5px', borderLeft: '4px solid #3498db' }}>
                                    <label style={{ margin: 0, color: '#333', lineHeight: '1.5' }}>
                                        <strong>Bài báo: </strong> {selectedArticleForEdit?.Title}
                                    </label>
                                </div>
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Nội dung cần chỉnh sửa (Sẽ gửi qua Email) *</label>
                                    <textarea
                                        className="form-input"
                                        rows="6"
                                        placeholder="Nhập chi tiết các lỗi hoặc phần minh chứng cần giảng viên bổ sung"
                                        value={editRequestContent}
                                        onChange={(e) => setEditRequestContent(e.target.value)}
                                        style={{ resize: 'vertical' }}
                                        required
                                    ></textarea>
                                </div>
                            </form>
                        </div>
                        <div className="modal-footer" style={{ borderTop: '1px solid #e2e8f0', padding: '15px 20px', display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'flex-end' }}>
                            <button type="button" className="btn-cancel" style={{ margin: 0 }} onClick={() => setIsEditRequestModalOpen(false)}>Hủy</button>
                            <button type="submit" form="editRequestForm" className="btn-submit" style={{ backgroundColor: '#9b59b6', margin: 0 }}>
                                <i className="fa-solid fa-paper-plane" style={{ marginRight: '8px' }}></i> Gửi Email
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {rejectModalOpen && (
                <div className="modal-overlay" style={{ zIndex: 1050 }}>
                    <div className="modal-box form-modal-box" style={{ maxWidth: '600px' }}>
                        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <h3 style={{ margin: 0, paddingRight: '15px', lineHeight: '1.4' }}>Lý do từ chối bài báo</h3>
                            <button className="close-btn" onClick={() => setRejectModalOpen(false)} style={{ fontSize: '26px', lineHeight: '1', display: 'flex', flexShrink: 0, marginTop: '-2px' }}>&times;</button>
                        </div>
                        <div className="modal-body" style={{ padding: '20px' }}>
                            <form id="rejectForm" onSubmit={handleSendReject}>
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Nội dung lý do (sẽ hiển thị cho giảng viên) *</label>
                                    <textarea
                                        className="form-input"
                                        rows="5"
                                        value={rejectReason}
                                        onChange={(e) => setRejectReason(e.target.value)}
                                        placeholder="Nhập lý do từ chối..."
                                        required
                                    />
                                </div>
                            </form>
                        </div>
                        <div className="modal-footer" style={{ borderTop: '1px solid #e2e8f0', padding: '15px 20px', display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'flex-end' }}>
                            <button type="button" className="btn-cancel" style={{ margin: 0 }} onClick={() => setRejectModalOpen(false)}>Hủy</button>
                            <button type="submit" form="rejectForm" className="btn-submit" style={{ backgroundColor: '#e74c3c', margin: 0 }}>
                                <i className="fa-solid fa-times-circle" style={{ marginRight: '8px' }}></i> Xác nhận từ chối
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isExportModalOpen && (
                <div className="modal-overlay" style={{ zIndex: 900 }}>
                    <div className="modal-box form-modal-box" style={{ maxWidth: '750px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <h3 style={{ margin: 0, paddingRight: '15px', lineHeight: '1.4' }}>Bộ Lọc & Xuất Excel nâng cao</h3>
                            <button className="close-btn" onClick={() => setIsExportModalOpen(false)} style={{ fontSize: '26px', lineHeight: '1', display: 'flex', flexShrink: 0, marginTop: '-2px' }}>&times;</button>
                        </div>
                        <div className="modal-body" style={{ padding: '20px', overflowY: 'auto' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '15px' }}>
                                <div className="form-group" style={{ flex: '1 1 250px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Từ ngày (Công bố)</label>
                                    <Calendar
                                        value={exportFilters.fromDate}
                                        onChange={(e) => setExportFilters({ ...exportFilters, fromDate: e.value })}
                                        placeholder="Từ ngày"
                                        dateFormat="dd/mm/yy"
                                        showIcon
                                        showClear
                                        style={{ width: '100%' }}
                                        inputStyle={{ paddingLeft: '12px', padding: '8px' }}
                                        appendTo={document.body}
                                    />
                                </div>
                                <div className="form-group" style={{ flex: '1 1 250px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Đến ngày (Công bố)</label>
                                    <Calendar
                                        value={exportFilters.toDate}
                                        onChange={(e) => setExportFilters({ ...exportFilters, toDate: e.value })}
                                        placeholder="Đến ngày"
                                        dateFormat="dd/mm/yy"
                                        showIcon
                                        showClear
                                        style={{ width: '100%' }}
                                        inputStyle={{ paddingLeft: '12px', padding: '8px' }}
                                        appendTo={document.body}
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                                <div className="form-group" style={{ flex: '1 1 250px', marginBottom: '15px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Xếp hạng Q-Ranking</label>
                                    <select className="form-input" style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                        value={exportFilters.qRanking} onChange={(e) => setExportFilters({ ...exportFilters, qRanking: e.target.value })}>
                                        <option value="">Toàn bộ</option>
                                        <option value="Q1">Q1</option>
                                        <option value="Q2">Q2</option>
                                        <option value="Q3">Q3</option>
                                        <option value="Q4">Q4</option>
                                    </select>
                                </div>
                                <div className="form-group" style={{ flex: '1 1 250px', marginBottom: '20px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Thành viên (Có trong tác giả)</label>
                                    <SearchableFocusDropdown
                                        value={exportFilters.userId}
                                        options={userOptions}
                                        onChange={(e) => setExportFilters({ ...exportFilters, userId: e.value })}
                                        placeholder="Toàn bộ thành viên"
                                        panelUniqueClass="custom-dropdown-panel-export-advanced-filter"
                                        className="custom-dropdown"
                                    />
                                </div>
                            </div>

                            {previewExportData && (
                                <div style={{ marginTop: '10px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
                                    <p style={{ margin: '0 0 10px 0', fontWeight: 'bold', color: '#1a9953' }}>
                                        <i className="fa-solid fa-table-list"></i> Xem trước dữ liệu: Tìm thấy {previewExportData.length} kết quả
                                    </p>

                                    <div style={{ maxHeight: '250px', overflowY: 'auto', overflowX: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                                        <table className="custom-table" style={{ width: '100%', minWidth: '500px', fontSize: '13px', margin: 0 }}>
                                            <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }}>
                                                <tr>
                                                    <th width="50px" style={{ textAlign: 'center' }}>STT</th>
                                                    <th>Tên bài báo</th>
                                                    <th width="150px">Tạp chí</th>
                                                    <th width="100px" style={{ textAlign: 'center' }}>Trạng thái</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {previewExportData.length > 0 ? (
                                                    previewExportData.map((item, idx) => (
                                                        <tr key={item.Id}>
                                                            <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                                                            <td><div style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{item.Title}</div></td>
                                                            <td><div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '140px' }}>{item.JournalName || '---'}</div></td>
                                                            <td style={{ textAlign: 'center' }}>{item.Status}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="4" style={{ textAlign: 'center', padding: '20px', color: '#888' }}>Không tìm thấy bài báo nào khớp với điều kiện lọc</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer" style={{ borderTop: '1px solid #eee', padding: '15px 20px', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '10px' }}>
                            <button type="button" className="btn-cancel" style={{ border: 'none', background: 'none', color: '#e74c3c', margin: 0 }} onClick={handleClearAdvancedFilters}>
                                <i className="fa-solid fa-eraser"></i> Xóa bộ lọc
                            </button>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                <button type="button" className="btn-submit" style={{ backgroundColor: '#3498db', margin: 0 }} onClick={handlePreviewAdvancedFilters}>
                                    <i className="fa-solid fa-magnifying-glass"></i> Lọc & Xem trước
                                </button>
                                <button type="button" className="btn-submit" style={{ backgroundColor: '#1a9953', margin: 0 }} onClick={handleExportFromModal}>
                                    <i className="fa-solid fa-download"></i> Xuất Excel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <DuplicateWarningModal
                isOpen={isDuplicateModalOpen}
                onClose={() => setIsDuplicateModalOpen(false)}
                duplicateGroups={duplicateGroups}
                type="article"
                onDelete={handleDelete}
                currentUserId={currentUser?.Id}
                isAdmin={true}
            />
        </div>
    );
};

export default ScientificArticlesMngt;